import {
  type InstallHealthStatus,
  IPCEventType,
  UpdateEventType,
  type UpdateStatus,
} from '@electron/shared';
import { getStores, useSDKState } from '@littlebird/sdk';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { DeviceCodeSignInModal } from '@/components/modals/DeviceCodeSignInModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { chatStore } from '@/features/chat/chat.store';
import { readAloudStore } from '@/features/chat/voice/read-aloud.store';
import { attachDesktopVoiceModePlatform } from '@/features/chat/voice/voice-mode-desktop-platform';
import { watchSpeechLifecycle } from '@/features/chat/voice/voice-mode-watchers';
import { electronApis } from '@/features/electron-api';
import {
  replaceHummingbirdDraftContext,
  stripHummingbirdModeAlias,
} from '@/features/hummingbird/hummingbird-mode.utils';
import { isSignInShownInline } from '@/features/local-mcp/use-outlook-sign-in';
import { useAutomaticMeetingPrep } from '@/features/meetings/meetings.hooks';
import { meetingApis } from '@/features/meetings/meetings.query';
import { apiLogsStore } from '@/features/request-logger/api-logs.store';
import { type RawApiLog } from '@/features/request-logger/api-logs.types';
import { persistRoutineDrafts } from '@/features/routines/routine-drafts-persistence';
import { playNotificationSoundsFromMain } from '@/features/sounds/notification-sounds';
import { tabStore, useTabs } from '@/features/tabs';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { logger } from '@/lib/logger';
import { littlebirdSdk } from '@/lib/sdk-init';
import { updateStore } from '@/stores/updateStore';
import { userStore } from '@/stores/user.store';
import { listenWithAbortIPC } from '@/utils/abort';
import { trackEvent } from '@/utils/analytics';
import { AppCapability, inElectron, isCapable } from '@/utils/capabilities';

import { parseOpenDeepLink } from './deep-link';
import { useHashRoute } from './useHashRoute';

function logUnexpected(event: 'active' | 'background') {
  return (error: unknown) => {
    logger.error(`Unexpected connection lifecycle failure on ${event}`, {
      error,
    });
  };
}

/** Install app-wide listeners, reactions, and polling for the authenticated app */
export const useGlobalEventListeners = () => {
  const isUserActiveRef = useRef(true);
  const awaySince = useRef(Date.now());
  const { isReady: isSdkReady } = useSDKState();
  const isInternalUser = useIsInternalUser();
  const router = useRouter();
  const { openTab } = useTabs();
  const { setHash } = useHashRoute();
  useAutomaticMeetingPrep();

  useLayoutEffect(() => {
    if (!isSdkReady) return;
    const detachVoiceModePlatform = attachDesktopVoiceModePlatform();
    const stopWatchingSpeechLifecycle = watchSpeechLifecycle();
    const stopPersistingRoutineDrafts = persistRoutineDrafts(
      getStores().routineDrafts
    );
    return () => {
      readAloudStore.stopCurrentRequest();
      detachVoiceModePlatform();
      stopWatchingSpeechLifecycle();
      stopPersistingRoutineDrafts();
    };
  }, [isSdkReady]);

  const handleDeepLink = useCallback(
    (path: string, search?: Record<string, string>) => {
      if (tabStore.showTabBar) {
        return openTab({ path, search }, { reuseExisting: true });
      } else {
        return router.navigate({ to: path, search });
      }
    },
    [openTab, router]
  );

  useEffect(() => {
    if (!inElectron()) return;
    const controller = new AbortController();
    listenWithAbortIPC(
      'main-window-focused',
      () => {
        trackEvent('MAIN_WINDOW_FOCUSED');
      },
      controller.signal
    );
    listenWithAbortIPC(
      'main-window-blurred',
      () => {
        trackEvent('MAIN_WINDOW_BLURRED');
      },
      controller.signal
    );
    playNotificationSoundsFromMain(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!inElectron()) return;
    const interval = setInterval(async () => {
      // A hands-free voice session produces no keyboard/mouse input; count it
      // as activity so the idle teardown cannot cut the session mid-reply
      const isUserActive =
        (await electronApis.system.isUserActive()) ||
        littlebirdSdk.voiceMode.session !== null;
      if (isUserActive === isUserActiveRef.current) return;
      if (isUserActive) {
        void littlebirdSdk.lifecycle
          .onActive({ inactiveMs: Date.now() - awaySince.current })
          .catch(logUnexpected('active'));
      } else {
        // `isUserActive` only flips once the OS idle threshold has passed, so
        // the user has already been gone that long: this is a lower bound.
        awaySince.current = Date.now();
        void littlebirdSdk.lifecycle
          .onBackground()
          .catch(logUnexpected('background'));
      }
      isUserActiveRef.current = isUserActive;
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (inElectron()) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void littlebirdSdk.lifecycle
          .onActive({ inactiveMs: Date.now() - awaySince.current })
          .catch(logUnexpected('active'));
      } else {
        awaySince.current = Date.now();
        void littlebirdSdk.lifecycle
          .onBackground()
          .catch(logUnexpected('background'));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!inElectron() || !isInternalUser) return;
    const controller = new AbortController();
    const handleApiLogPayload = (payload: RawApiLog) =>
      apiLogsStore.addLogs(payload);

    listenWithAbortIPC(
      IPCEventType.apiSenderEventReceived,
      handleApiLogPayload,
      controller.signal
    );
    return () => controller.abort();
  }, [isInternalUser]);

  useEffect(() => {
    if (!inElectron()) return;
    const unsubscribeMeetingListeners = meetingApis.setupMainWindowListeners();
    return () => {
      unsubscribeMeetingListeners();
    };
  }, []);

  useEffect(() => {
    if (!inElectron()) return;
    return electronApis.axonMcp.onDeviceCodePrompt((challenge) => {
      if (isSignInShownInline(challenge.slug)) return;
      ModalsManager.show(
        DeviceCodeSignInModal,
        { url: challenge.url, code: challenge.code, slug: challenge.slug },
        { unique: true }
      );
    });
  }, []);

  useEffect(() => {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) return;
    const controller = new AbortController();

    listenWithAbortIPC<{ url: string }>(
      'deep-link',
      async (event) => {
        const deepLink = event.url;
        if (!deepLink?.includes('/open')) return;

        const parsedDeepLink = parseOpenDeepLink(deepLink);
        if (!parsedDeepLink) return;

        const { path, hash, search, shouldNavigate } = parsedDeepLink;

        if (shouldNavigate) {
          await handleDeepLink(path, search);
        }

        if (hash) setHash(hash);
      },
      controller.signal
    );

    return () => controller.abort();
  }, [handleDeepLink, setHash]);

  useEffect(() => {
    return listenWithAbortIPC<{
      threadId: string;
      draftContent?: string;
      chatMode?: string;
      draftContext?: string;
    }>('expand-thread-to-main-window', async (event) => {
      const { drafts, threads } = getStores();
      await threads.ensureThreadIsReady(event.threadId);
      if (event.draftContent !== undefined) {
        await drafts.updateDraft({
          draftId: event.threadId,
          content: event.draftContent,
        });
      }
      const existingSelections =
        drafts.getDraft(event.threadId)?.selectedTexts ?? [];
      const selectedTexts = replaceHummingbirdDraftContext(
        existingSelections,
        event.draftContext
      );
      const hadHummingbirdContext =
        selectedTexts.length < existingSelections.length;
      if (hadHummingbirdContext || event.draftContext?.trim()) {
        await drafts.updateDraft({
          draftId: event.threadId,
          selectedTexts,
        });
      }
      const chatMode = stripHummingbirdModeAlias(event.chatMode);
      if (chatMode) {
        chatStore.setChatMode(
          chatMode,
          event.threadId,
          undefined,
          userStore.user?.allowed_chat_modes
        );
      }
      handleDeepLink(`/chats/${event.threadId}`);
    });
  }, [handleDeepLink]);

  useEffect(() => {
    if (!isCapable(AppCapability.AutoUpdate)) return;

    updateStore.initReactions();

    const controller = new AbortController();
    listenWithAbortIPC<UpdateStatus>(
      UpdateEventType.UpdateStatusChanged,
      (status) => updateStore.handleUpdateStatus(status),
      controller.signal
    );
    listenWithAbortIPC<InstallHealthStatus>(
      UpdateEventType.InstallHealthChanged,
      (health) => updateStore.handleInstallHealth(health),
      controller.signal
    );
    void updateStore.syncInstallHealth();
    void updateStore.syncUpdateStatus();

    return () => {
      controller.abort();
      updateStore.cleanup();
    };
  }, []);

  return null;
};
